import { useSyncExternalStore } from 'react';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
  type UseMutateAsyncFunction,
  type UseMutateFunction,
  type UseMutationResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  toCampaignConfigUpsert,
  type CampaignConfigDirtyFields,
  type CampaignConfigFormValues,
} from '@/lib/campaign/campaign-config';
import { supabase } from '@/lib/supabase';

const UPDATE_CAMPAIGN_CONFIG_MUTATION_KEY = [
  'update-campaign-config',
] as const;

const CAMPAIGN_CONFIG_QUERY_PREFIXES = [
  ['campaign-pulse'],
  ['campaign-detail'],
  ['conversation-threads'],
  ['conversation-thread'],
  ['conversation-campaigns'],
  ['campaign-config'],
  ['draft-campaign-domain'],
  ['lead-dossier'],
] as const;

// This registry is deliberately independent of TanStack's mutation cache.
// AuthContext clears that cache synchronously on an identity switch, while
// the shared campaign-config write may still be in flight. Keeping the count
// here prevents a remounted Edit/Save control from starting a second write.
let pendingCampaignConfigWrites = 0;
const pendingCampaignConfigWriteListeners = new Set<() => void>();

function notifyPendingCampaignConfigWriteListeners(): void {
  pendingCampaignConfigWriteListeners.forEach((listener) => listener());
}

function incrementPendingCampaignConfigWrites(): void {
  pendingCampaignConfigWrites += 1;
  notifyPendingCampaignConfigWriteListeners();
}

function decrementPendingCampaignConfigWrites(): void {
  pendingCampaignConfigWrites = Math.max(0, pendingCampaignConfigWrites - 1);
  notifyPendingCampaignConfigWriteListeners();
}

function subscribePendingCampaignConfigWrites(listener: () => void): () => void {
  pendingCampaignConfigWriteListeners.add(listener);
  return () => {
    pendingCampaignConfigWriteListeners.delete(listener);
  };
}

function getPendingCampaignConfigWritesSnapshot(): number {
  return pendingCampaignConfigWrites;
}

function registerPendingCampaignConfigWrite<T>(start: () => T): T {
  incrementPendingCampaignConfigWrites();
  try {
    return start();
  } catch (error) {
    // A synchronous observer/setup failure never reaches onSettled, so pair
    // the registration here. Normal async success/failure decrements there.
    decrementPendingCampaignConfigWrites();
    throw error;
  }
}

export interface UpdateCampaignConfigVariables {
  campaignId: string;
  campaignOrganizationId: string;
  formValues: CampaignConfigFormValues;
  dirtyFields: CampaignConfigDirtyFields;
}

export type UpdateCampaignConfigMutation = UseMutationResult<
  void,
  Error,
  UpdateCampaignConfigVariables
> & {
  canUpdate: boolean;
};

export function useUpdateCampaignConfig(): UpdateCampaignConfigMutation {
  const { user, organizationId } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation<void, Error, UpdateCampaignConfigVariables>({
    mutationKey: UPDATE_CAMPAIGN_CONFIG_MUTATION_KEY,
    mutationFn: async ({
      campaignId,
      campaignOrganizationId,
      formValues,
      dirtyFields,
    }): Promise<void> => {
      if (!organizationId) {
        throw new Error(
          'Campaign settings require an organization context.',
        );
      }

      // The acted-on campaign row must belong to the CURRENT session org.
      // Never translate a foreign/stale row into a write under this session.
      if (campaignOrganizationId !== organizationId) {
        throw new Error(
          'Campaign settings refused: campaign organization does not match the session organization.',
        );
      }

      const payload = toCampaignConfigUpsert(formValues, dirtyFields, {
        organizationId,
        campaignId,
      });

      const hasDirtyIdentity =
        dirtyFields.campaign_name === true || dirtyFields.product_line === true;
      if (hasDirtyIdentity && !payload) {
        throw new Error('Campaign settings contain invalid identity values.');
      }

      // An untouched form remains a true no-op at the hook boundary too.
      if (!payload) return;

      const { error } = await supabase
        .from('os_campaign_config')
        .upsert(payload, {
          onConflict: 'organization_id,heyreach_campaign_id',
        });

      if (error) throw error;
    },
    onError: (): void => {
      toast.error('Campaign settings could not be saved.');
    },
    onSuccess: (): void => {
      toast.success('Campaign settings saved.');
    },
    onSettled: async (): Promise<void> => {
      // Decrement first so a cache operation can never strand the registry.
      decrementPendingCampaignConfigWrites();

      // Root prefixes intentionally target whatever viewer's cache exists at
      // settle time. Cancellation must finish before any invalidation starts:
      // invalidation alone can reuse a first-ever in-flight pre-write fetch.
      // The draft campaign domain and lead dossier both materialize campaign
      // names, so a rename must fan out to them too. Reply Queue row/count
      // caches deliberately stay out: they carry only a campaign id, never a
      // materialized name.
      await Promise.all(
        CAMPAIGN_CONFIG_QUERY_PREFIXES.map((queryKey) =>
          queryClient.cancelQueries({ queryKey }),
        ),
      );

      await Promise.all(
        CAMPAIGN_CONFIG_QUERY_PREFIXES.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        ),
      );
    },
  });

  // Register synchronously at the public submit boundary, before TanStack's
  // async execute() reaches mutationFn. This closes the small clear/remount
  // window where the mutation cache can disappear before the request starts.
  const mutate: UseMutateFunction<
    void,
    Error,
    UpdateCampaignConfigVariables
  > = (variables, options): void => {
    registerPendingCampaignConfigWrite(() =>
      mutation.mutate(variables, options),
    );
  };
  const mutateAsync: UseMutateAsyncFunction<
    void,
    Error,
    UpdateCampaignConfigVariables
  > = (variables, options): Promise<void> =>
    registerPendingCampaignConfigWrite(() =>
      mutation.mutateAsync(variables, options),
    );

  return {
    ...mutation,
    mutate,
    mutateAsync,
    canUpdate: organizationId !== null && user !== null,
  };
}

export function useIsUpdateCampaignConfigPending(): boolean {
  const registryPending = useSyncExternalStore(
    subscribePendingCampaignConfigWrites,
    getPendingCampaignConfigWritesSnapshot,
    getPendingCampaignConfigWritesSnapshot,
  );

  return (
    useIsMutating({ mutationKey: UPDATE_CAMPAIGN_CONFIG_MUTATION_KEY }) > 0 ||
    registryPending > 0
  );
}
